import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, AlertTriangle, ArrowLeft, Loader2, Plus, X } from 'lucide-react';
import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { useAuthStore } from '../../auth/authStore';
import { ApiError } from '../../../shared/api/httpClient';
import { useI18nStore } from '../../../shared/i18n/i18nStore';
import type { MessageKey } from '../../../shared/i18n/messages';
import { submitFeedback } from './api';
import { compressImage, MAX_IMAGE_BYTES } from './compressImage';
import { clearFeedbackDraft, readFeedbackDraft, saveFeedbackDraft, type DraftStore } from './feedbackDraft';
import { feedbackErrorCopyKey } from './feedbackErrorCopy';
import { bumpSlotGeneration, isCurrentSlotGeneration } from './feedbackImageSlotGeneration';

// Mirrors FeedbackSubmissionRules.MIN_CONTENT / MAX_CONTENT (server-side
// source of truth) so the submit button can disable itself immediately —
// same convention as MAX_IMAGE_BYTES in compressImage.ts.
const MIN_CONTENT_LENGTH = 10;
const MAX_CONTENT_LENGTH = 1000;
const MAX_IMAGES = 3;

type ImageSlot =
  | { status: 'compressing' }
  | { status: 'ready'; blob: Blob; url: string }
  | { status: 'error'; messageKey: MessageKey };

type Slots = (ImageSlot | null)[];

/** window.localStorage, guarded: the getter itself can throw in some private-browsing modes. */
function safeLocalStorage(): DraftStore | undefined {
  try {
    return typeof window === 'undefined' ? undefined : window.localStorage;
  } catch {
    return undefined;
  }
}

function replaceAt<T>(list: T[], index: number, value: T): T[] {
  const next = list.slice();
  next[index] = value;
  return next;
}

/**
 * Full-screen feedback compose (task-16 brief: a modal makes attaching
 * screenshots miserable, so this is a screen, not a sheet). Reached only
 * through FeedbackPanel's "submit feedback" button, which already refuses
 * to open this screen without a token — see that file for the guard. If a
 * token this screen was opened with expires before submit, the server's
 * 401 flows through the same error-code mapping as any other failure and
 * shows feedbackLoginRequired, so no separate "not signed in" branch is
 * needed here.
 */
export function FeedbackCompose({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
  const token = useAuthStore((state) => state.token);
  const t = useI18nStore((state) => state.t);
  const queryClient = useQueryClient();

  const [content, setContent] = useState(() => readFeedbackDraft(safeLocalStorage()));
  // Captured once, at mount: true only when this screen opened with text
  // restored from a previous, abandoned attempt. Images can never be part of
  // that restore (a Blob can't survive localStorage — see feedbackDraft.ts),
  // so this is what drives the "your images weren't saved" note below, not
  // a failed submit (nothing is lost at that point — the thumbnails are
  // still right there on screen and resubmitting works with the same ones).
  const [hadRestoredDraft] = useState(() => content.length > 0);
  const [slots, setSlots] = useState<Slots>(() => Array.from({ length: MAX_IMAGES }, () => null));

  // Revoke every object URL this screen ever created, on removal AND on
  // unmount — a player who picks and discards images while composing would
  // otherwise leak one blob URL per image for the rest of the session (§2
  // of the brief). A ref (not `slots` in the deps array) so this only runs
  // once, on true unmount, but still reads the latest slots.
  const slotsRef = useRef<Slots>(slots);
  slotsRef.current = slots;
  useEffect(() => {
    return () => {
      slotsRef.current.forEach((slot) => {
        if (slot?.status === 'ready') URL.revokeObjectURL(slot.url);
      });
    };
  }, []);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const pendingIndexRef = useRef<number | null>(null);
  // One counter per slot, bumped whenever that slot's identity changes (a
  // new pick starts, or the slot is cleared) — see
  // feedbackImageSlotGeneration.ts. Lets a compression that's still running
  // when the player removes that slot recognize, once it resolves, that its
  // result is stale and must be discarded instead of resurrecting the image.
  const slotGenerationsRef = useRef<number[]>(Array.from({ length: MAX_IMAGES }, () => 0));

  function handleContentChange(event: ChangeEvent<HTMLTextAreaElement>) {
    const value = event.target.value;
    setContent(value);
    saveFeedbackDraft(safeLocalStorage(), value);
  }

  function pickImage(index: number) {
    pendingIndexRef.current = index;
    fileInputRef.current?.click();
  }

  function handleFileInputChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0] ?? null;
    const index = pendingIndexRef.current;
    // Reset immediately so picking the exact same file twice in a row still
    // fires a change event the second time.
    event.target.value = '';
    pendingIndexRef.current = null;
    if (!file || index == null) return;
    void addImageAtIndex(index, file);
  }

  // Compress on selection, not on submit (§1 of the brief) — a phone
  // screenshot is routinely 4-6MB, and doing this eagerly means the player
  // sees a working thumbnail in seconds instead of waiting until they tap
  // Submit. Only a still-too-large compressed result rejects this one slot;
  // everything else (other slots, typed text) is untouched.
  async function addImageAtIndex(index: number, file: File) {
    const { generations, generation } = bumpSlotGeneration(slotGenerationsRef.current, index);
    slotGenerationsRef.current = generations;
    setSlots((prev) => replaceAt(prev, index, { status: 'compressing' }));
    try {
      const { blob } = await compressImage(file);
      // The player may have removed (or re-picked) this slot while
      // compression was still running — a stale result must never be
      // applied, or a deliberately removed image reappears out of nowhere.
      if (!isCurrentSlotGeneration(slotGenerationsRef.current, index, generation)) return;
      if (blob.size > MAX_IMAGE_BYTES) {
        setSlots((prev) => replaceAt(prev, index, { status: 'error', messageKey: 'feedbackErrImageTooLarge' }));
        return;
      }
      const url = URL.createObjectURL(blob);
      if (!isCurrentSlotGeneration(slotGenerationsRef.current, index, generation)) {
        URL.revokeObjectURL(url); // abandoned — don't strand it unrevoked
        return;
      }
      setSlots((prev) => replaceAt(prev, index, { status: 'ready', blob, url }));
    } catch {
      if (!isCurrentSlotGeneration(slotGenerationsRef.current, index, generation)) return;
      setSlots((prev) => replaceAt(prev, index, { status: 'error', messageKey: 'feedbackErrImageType' }));
    }
  }

  function removeImage(index: number) {
    // Invalidates any compression still in flight for this slot — see
    // addImageAtIndex's generation check above.
    slotGenerationsRef.current = bumpSlotGeneration(slotGenerationsRef.current, index).generations;
    setSlots((prev) => {
      const slot = prev[index];
      if (slot?.status === 'ready') URL.revokeObjectURL(slot.url);
      return replaceAt(prev, index, null);
    });
  }

  const submitMutation = useMutation({
    mutationFn: () => {
      const images = slots
        .filter((slot): slot is Extract<ImageSlot, { status: 'ready' }> => slot?.status === 'ready')
        .map((slot) => slot.blob);
      return submitFeedback(token || '', content.trim(), images);
    },
    onSuccess: () => {
      // Only a successful submit clears the draft (§3 of the brief) — a
      // failed attempt below leaves it in place.
      clearFeedbackDraft(safeLocalStorage());
      queryClient.invalidateQueries({ queryKey: ['feedback'] });
      queryClient.invalidateQueries({ queryKey: ['feedback-summary'] });
      onDone();
    },
  });

  const trimmedLength = content.trim().length;
  const isCompressing = slots.some((slot) => slot?.status === 'compressing');
  const canSubmit =
    trimmedLength >= MIN_CONTENT_LENGTH &&
    trimmedLength <= MAX_CONTENT_LENGTH &&
    !isCompressing &&
    !submitMutation.isPending;

  const submitError = submitMutation.error;
  const submitErrorKey = submitError
    ? feedbackErrorCopyKey(submitError instanceof ApiError ? submitError.message : undefined)
    : null;

  return (
    <div className="feedback-compose-screen">
      <header className="feedback-compose-header">
        <button type="button" className="icon-button" onClick={onCancel} aria-label={t('back')}>
          <ArrowLeft size={20} aria-hidden="true" />
        </button>
        <h2>{t('feedbackNew')}</h2>
      </header>

      <div className="feedback-compose-field">
        <textarea
          className="feedback-compose-textarea"
          value={content}
          maxLength={MAX_CONTENT_LENGTH}
          placeholder={t('feedbackPlaceholder')}
          onChange={handleContentChange}
        />
        <span className={`feedback-compose-counter ${trimmedLength > MAX_CONTENT_LENGTH - 40 ? 'is-near-limit' : ''}`}>
          {content.length}/{MAX_CONTENT_LENGTH}
        </span>
      </div>

      <p className="feedback-compose-images-label">{t('feedbackImagesLabel')}</p>
      <div className="feedback-image-grid feedback-compose-images">
        {slots.map((slot, index) => (
          <FeedbackComposeTile
            key={index}
            slot={slot}
            onPick={() => pickImage(index)}
            onRemove={() => removeImage(index)}
            t={t}
          />
        ))}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="feedback-compose-file-input"
        onChange={handleFileInputChange}
        aria-hidden="true"
        tabIndex={-1}
      />

      {hadRestoredDraft && slots.every((slot) => slot == null) ? (
        <p className="feedback-compose-draft-note">{t('feedbackErrImagesNotSaved')}</p>
      ) : null}

      {slots.some((slot) => slot?.status === 'error') ? (
        <ul className="feedback-compose-image-errors">
          {slots.map((slot, index) =>
            slot?.status === 'error' ? <li key={index}>{t(slot.messageKey)}</li> : null,
          )}
        </ul>
      ) : null}

      {submitErrorKey ? (
        <div className="feedback-compose-error" role="alert">
          <AlertTriangle size={16} aria-hidden="true" />
          <p>{t(submitErrorKey)}</p>
        </div>
      ) : null}

      <button
        type="button"
        className="feedback-compose-submit"
        disabled={!canSubmit}
        onClick={() => submitMutation.mutate()}
      >
        {submitMutation.isPending ? <Loader2 size={16} className="feedback-compose-spin" aria-hidden="true" /> : null}
        {t('feedbackSubmit')}
      </button>
    </div>
  );
}

function FeedbackComposeTile({
  slot,
  onPick,
  onRemove,
  t,
}: {
  slot: ImageSlot | null;
  onPick: () => void;
  onRemove: () => void;
  t: ReturnType<typeof useI18nStore.getState>['t'];
}) {
  if (slot == null) {
    return (
      <button type="button" className="feedback-compose-tile is-empty" onClick={onPick} aria-label={t('feedbackImagesLabel')}>
        <Plus size={20} aria-hidden="true" />
      </button>
    );
  }

  if (slot.status === 'compressing') {
    return (
      <div className="feedback-compose-tile is-compressing">
        <Loader2 size={18} className="feedback-compose-spin" aria-hidden="true" />
      </div>
    );
  }

  if (slot.status === 'error') {
    return (
      <button type="button" className="feedback-compose-tile is-error" onClick={onPick} aria-label={t(slot.messageKey)}>
        <AlertCircle size={18} aria-hidden="true" />
      </button>
    );
  }

  return (
    <div className="feedback-compose-tile is-ready">
      <img src={slot.url} alt="" />
      <button type="button" className="feedback-compose-tile-remove" onClick={onRemove} aria-label={t('cancel')}>
        <X size={13} aria-hidden="true" />
      </button>
    </div>
  );
}
